import React, { FC, useEffect, useState, useRef } from 'react';
import { EditorContent, useEditor, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Underline } from '@tiptap/extension-underline';
import CharacterCount from '@tiptap/extension-character-count';
import styled from 'styled-components';
import Select, { StylesConfig, CSSObjectWithLabel } from 'react-select';
import {
    BoldIcon,
    ItalicIcon,
    UnderlineIcon,
    UnorderedListIcon,
    OrderedListIcon,
    UndoIcon,
    RedoIcon,
} from '../assets';
import * as awsui from '@amzn/awsui-design-tokens';

const limit = 3000;

const ToolbarWrapper = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-start;
    border: 1px solid ${awsui.colorBorderDividerDefault};             
    border-radius: 3px;
    margin-bottom: -1px;
    padding: 0px 8px;
`;

const TextEditorWrapper = styled.div`
    position: relative;
    z-index: 2;
`;

const CharacterLimit = styled.div`
    color: #868e96;
    margin-top: 8px;
    margin-left: auto;
    margin-right: 10px;
`;

interface ToolbarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    active?: boolean;
}

const ToolbarButton = styled.button<ToolbarButtonProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 5px;
    background-color: ${awsui.colorBackgroundContainerContent};
    color: ${(props) => (props.active ? '#1998d5' : awsui.colorTextFormDefault)};
    border: none;
    cursor: pointer;
    padding: 0;
    /* Remove default button styles */
    font: inherit;

    &:focus {
        outline: none;
    }
`;

interface TextEditorProps {
    value: string;
    onChange: (value: string) => void;
}

const useTextEditor = (initialValue: string, onChange: (value: string) => void) => {
    const editorRef = useRef<Editor | null>(null);
    const [content, setContent] = useState(initialValue);

    const editor = useEditor({
        extensions: [
            StarterKit,
            Link,
            Underline,
            CharacterCount.configure({
                limit,
            }),
        ],
        content: content,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            setContent(html);
            onChange(html);
        },
    });

    useEffect(() => {
        const editorInstance = editorRef.current;
        if (editorInstance) {
            editorInstance.commands.setContent(initialValue);
        }
    }, [initialValue]);

    return {
        editor,
        editorRef,
    };
};

interface ToolbarProps {
    editor: Editor | null;
}

const Toolbar: FC<ToolbarProps> = ({ editor }) => {
    if (!editor) return null;

    const handleBold = () => {
        editor.chain().focus().toggleBold().run();
    };

    const handleItalic = () => {
        editor.chain().focus().toggleItalic().run();
    };

    const handleUnderline = () => {
        editor.chain().focus().toggleUnderline().run();
    };

    const handleBulletList = () => {
        editor.chain().focus().toggleBulletList().run();
    };

    const handleNumberedList = () => {
        editor.chain().focus().toggleOrderedList().run();
    };

    const handleTextStyle = (selectedOption: any) => {
        const value = selectedOption?.value;
        switch (value) {
            case 'heading-small':
                editor.chain().focus().toggleHeading({ level: 3 }).run();
                break;
            case 'heading-medium':
                editor.chain().focus().toggleHeading({ level: 2 }).run();
                break;
            case 'heading-large':
                editor.chain().focus().toggleHeading({ level: 1 }).run();
                break;
            case 'normal':
            default:
                editor.chain().focus().setParagraph().run();
                break;
        }
    };

    const handleUndo = () => {
        editor.chain().focus().undo().run();
    };

    const handleRedo = () => {
        editor.chain().focus().redo().run();
    };

    const textStyleOptions = [
        { value: 'normal', label: 'Normal' },
        { value: 'heading-small', label: 'Heading Small' },
        { value: 'heading-medium', label: 'Heading Medium' },
        { value: 'heading-large', label: 'Heading Large' },
    ];

    // Dropdown Styling
    const dropdownStyle: StylesConfig = {
        control: (provided: CSSObjectWithLabel) => ({
            ...provided,
            height: '32px',
            minHeight: '32px',
            width: '200px',
            cursor: 'pointer',
            textAlign: 'left',
            margin: '5px',
            backgroundColor: awsui.colorBackgroundContainerContent,
            color: awsui.colorTextFormDefault,
        }),
        menu: (provided: CSSObjectWithLabel) => ({
            ...provided,
            zIndex: 20, // Needed for Table Header
            textAlign: 'left',
            backgroundColor: awsui.colorBackgroundContainerContent,
            color: awsui.colorTextFormDefault,
        }),
        singleValue: (provided: CSSObjectWithLabel) => ({
            ...provided,
            paddingBottom: '5px',
            backgroundColor: awsui.colorBackgroundContainerContent,
            color: awsui.colorTextFormDefault,
        }),
        placeholder: (provided: CSSObjectWithLabel) => ({
            ...provided,
            paddingBottom: '5px',
        }),
    };

    return (
        <ToolbarWrapper>
            <ToolbarButton onClick={handleBold} active={editor.isActive('bold')}>
                <BoldIcon />
            </ToolbarButton>
            <ToolbarButton onClick={handleItalic} active={editor.isActive('italic')}>
                <ItalicIcon />
            </ToolbarButton>
            <ToolbarButton onClick={handleUnderline} active={editor.isActive('underline')}>
                <UnderlineIcon />
            </ToolbarButton>
            <ToolbarButton onClick={handleBulletList} active={editor.isActive('bulletList')}>
                <UnorderedListIcon />
            </ToolbarButton>
            <ToolbarButton onClick={handleNumberedList} active={editor.isActive('orderedList')}>
                <OrderedListIcon />
            </ToolbarButton>
            <Select
                options={textStyleOptions}
                onChange={handleTextStyle}
                placeholder='Normal'
                styles={dropdownStyle}
            />
            <ToolbarButton onClick={handleUndo}>
                <UndoIcon />
            </ToolbarButton>
            <ToolbarButton onClick={handleRedo}>
                <RedoIcon />
            </ToolbarButton>
            <CharacterLimit>
                {editor.storage.characterCount.characters()}/{limit}
            </CharacterLimit>
        </ToolbarWrapper>
    );
};

const TextEditor: FC<TextEditorProps> = ({ value, onChange }) => {
    const { editor, editorRef } = useTextEditor(value, onChange);

    return (
        <TextEditorWrapper>
            <Toolbar editor={editor} />
            <EditorContent editor={editor} className='text-editor' />
        </TextEditorWrapper>
    );
};

export default TextEditor;
